import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className='text-2xl font-semibold mb-4'>{title}</h2>
    <div className='text-gray-300 space-y-3'>{children}</div>
  </section>
);

const MemsPrivacyPolicy = () => {
  return (
    <div className='min-h-screen bg-black text-white p-8'>
      <div className='max-w-3xl mx-auto'>
        <h1 className='text-4xl font-bold mb-2'>Mems Privacy Policy</h1>
        <p className='text-gray-500 mb-8'>Last updated October 4, 2026</p>

        <div className='space-y-8'>
          <p className='text-gray-300'>
            Mems ("we," "us," or "the app") turns the photos you took on this day in past years into
            mini-movies. It is built so that your memories stay yours. This policy explains what the
            Mems iOS app accesses, how it's used, and what is — and isn't — shared. Mems is developed by
            Bobby Boy Holdings, Inc. (Anyone Labs).
          </p>

          <Section title='1. Your photos stay on your device'>
            <p>
              Mems reads your photo library only to find "on this day" memories and build mini-movies.
              Grouping photos into memories, place lookups, people matching, captions, and movie assembly
              all happen <strong className='text-white'>on your iPhone</strong>. Your photo library is never
              uploaded to our servers. The only photo or video content that leaves your device is the limited
              content of a memory you choose to share (see section 4).
            </p>
          </Section>

          <Section title='2. On-device people matching'>
            <p>
              Mems detects faces in your photos and creates numerical face measurements, small face crops,
              and a local record of which photo contained which person, so it can recognize the same person
              across years of your own memories. This is on by default and stays entirely on your device in
              the app's protected storage. It is never sent to our servers, iCloud, friends, widgets,
              analytics, notifications, or advertising systems.
            </p>
            <p>
              An automatic match is only a local suggestion — it can never share a memory until you confirm
              that person. You can turn people matching off at any time in Mems Settings, which deletes the
              stored face measurements, crops, and inferred tags from your device.
            </p>
          </Section>

          <Section title='3. Captions'>
            <p>
              Mems shows captions for speech in your videos using Apple's on-device speech recognition. The
              audio is processed on your iPhone, and the resulting captions are stored only on your device.
            </p>
          </Section>

          <Section title='4. What gets shared, and only by your choice'>
            <p>
              <strong className='text-white'>No account is required</strong> to use Mems. You only sign in —
              with your phone number, verified by a one-time SMS code — if you want to share memories with
              friends.
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li>
                <strong className='text-white'>Shared memories.</strong> When you share a memory, only the
                still images, trimmed motion clips, and playback recipe needed to assemble that one movie are
                synced through your Apple iCloud (CloudKit) account to the friends you choose. Face data,
                photo identifiers, and the rest of your library are never included.
              </li>
              <li>
                <strong className='text-white'>Account.</strong> When you sign in we store your phone number
                (and a hash of it used for friend discovery), the display name you choose, and identifiers
                needed to sync with friends and send you notifications.
              </li>
              <li>
                <strong className='text-white'>Finding friends.</strong> If you allow Contacts access, Mems
                hashes your contacts' phone numbers on your device and sends only the hashes — never names or
                raw numbers — for a one-time lookup of friends already on Mems. Hashes that don't match a
                member are not retained; only actual member-to-member connections are saved.
              </li>
              <li>
                <strong className='text-white'>Invitations.</strong> If you invite someone who isn't on Mems,
                an invitation can be sent by text message to the number you provide, at your request only.
              </li>
            </ul>
          </Section>

          <Section title='5. Text message (SMS) invitations'>
            <p>
              Mobile numbers used for sign-in codes and invitations are{" "}
              <strong className='text-white'>
                not shared, sold, or rented to third parties or affiliates for marketing or promotional
                purposes
              </strong>
              . Invitation messages are only sent at your request; message frequency varies. Message and data
              rates may apply. Recipients can reply STOP to opt out or HELP for help.
            </p>
          </Section>

          <Section title='6. Analytics and diagnostics'>
            <p>
              We use PostHog for product analytics (for example app opens, onboarding steps, tags, shares, and
              widget status) and crash reports, and Datadog for performance and diagnostic logs. These
              events contain opaque identifiers, counts, outcomes, and app build information — never photos,
              face data, captions, contact names, or phone numbers. Product events are linked to your Mems
              account identifier when you're signed in. We do not use this data for advertising or tracking
              across other companies' apps or websites.
            </p>
          </Section>

          <Section title='7. What we do not do'>
            <ul className='list-disc pl-6 space-y-2'>
              <li>We do not sell your data.</li>
              <li>We do not track you across other apps or websites, and we show no ads.</li>
              <li>We do not use your photos to train models.</li>
              <li>We do not upload your photo library, face data, contact names, or raw contact numbers.</li>
            </ul>
          </Section>

          <Section title='8. Permissions'>
            <p>
              Mems asks for access to Photos (required to find memories), and optionally Contacts and
              notifications. Each is used only for the feature it powers, and you can change them anytime in
              iOS Settings.
            </p>
          </Section>

          <Section title='9. Retention and deletion'>
            <p>
              Memories live on your device. Shared-memory content stays in iCloud only as long as needed for
              the people you shared with, and you can revoke a share in the app. You can permanently delete
              your Mems account, friend connections, invitations, and device tokens from Mems Settings. For
              help with any data request, email us.
            </p>
          </Section>

          <Section title='10. Children'>
            <p>Mems is not directed to children under 13, and we do not knowingly collect their data.</p>
          </Section>

          <Section title='11. Changes'>
            <p>
              We may update this policy. Material changes will be reflected by the "last updated" date above.
            </p>
          </Section>

          <Section title='12. Contact'>
            <p>
              Questions or requests:{" "}
              <a href='mailto:privacy@sharemems.app' className='text-orange-400 hover:underline'>
                privacy@sharemems.app
              </a>
            </p>
          </Section>
        </div>

        <div className='mt-12 flex gap-6 text-gray-400'>
          <Link to='/mems/terms' className='hover:text-white'>Terms of Service</Link>
          <Link to='/' className='hover:text-white'>Anyone Labs</Link>
        </div>
      </div>
    </div>
  );
};

export default MemsPrivacyPolicy;
